import * as React from 'react'
import {
  DateRangePicker as AriaDateRangePicker,
  Group,
  Button as AriaButton,
  Popover,
  Dialog,
  DateInput as AriaDateInput,
  type DateRangePickerProps as AriaDateRangePickerProps,
  type DateValue,
  type RangeValue,
} from 'react-aria-components'
import {
  today,
  getLocalTimeZone,
  startOfMonth,
  endOfMonth,
} from '@internationalized/date'
import { CalendarBlank } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { DateSegment } from './date-field'
import { RangeCalendar } from './range-calendar'
import { Button } from './button'

/**
 * DateRangePicker — like `DatePicker`, but the input holds *two* segmented spots
 * (start – end) and the dropdown is a `RangeCalendar`. Built on
 * react-aria-components.
 *
 *   <DateRangePicker value={range} onChange={setRange} />
 *
 * Two reporting variants via props:
 *  - `presets` — a quick-select column on the right (`true` for the built-in set:
 *    Today / Yesterday / Last 7·30 days / This·Last month, or pass your own).
 *  - `withApply` — an Apply / Cancel footer that *gates* the commit: selecting a
 *    range edits an internal draft and only fires `onChange` on Apply; Cancel
 *    reverts. (Without it, selection commits live as usual.)
 *
 * Boolean props map to react-aria's `is*`.
 */

type FieldSize = 'sm' | 'md' | 'lg'
type FieldStatus = 'default' | 'error' | 'success'
type Range = RangeValue<DateValue> | null

export interface DateRangePreset {
  label: string
  value: RangeValue<DateValue>
}

const groupSize: Record<FieldSize, string> = {
  sm: 'h-8 pl-2.5 pr-1 text-xs',
  md: 'h-9 pl-3 pr-1 text-sm',
  lg: 'h-10 pl-3.5 pr-1 text-base',
}

const groupBase =
  'inline-flex w-fit items-center gap-1.5 rounded-lg border bg-fbui-base-white text-fbui-gray-800 transition-colors focus-within:ring-4 data-[disabled]:cursor-not-allowed data-[disabled]:bg-fbui-gray-50 data-[disabled]:text-fbui-text-disabled'

const groupStatus = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-600 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-600 focus-within:ring-fbui-ring-success',
} as const

/** The built-in quick-select ranges (computed against today). */
const buildDefaultPresets = (): DateRangePreset[] => {
  const now = today(getLocalTimeZone())
  return [
    { label: 'Today', value: { start: now, end: now } },
    {
      label: 'Yesterday',
      value: {
        start: now.subtract({ days: 1 }),
        end: now.subtract({ days: 1 }),
      },
    },
    {
      label: 'Last 7 days',
      value: { start: now.subtract({ days: 6 }), end: now },
    },
    {
      label: 'Last 30 days',
      value: { start: now.subtract({ days: 29 }), end: now },
    },
    {
      label: 'This month',
      value: { start: startOfMonth(now), end: endOfMonth(now) },
    },
    {
      label: 'Last month',
      value: {
        start: startOfMonth(now.subtract({ months: 1 })),
        end: endOfMonth(now.subtract({ months: 1 })),
      },
    },
  ]
}

const sameRange = (a: Range, b: Range): boolean =>
  !!a && !!b && a.start.compare(b.start) === 0 && a.end.compare(b.end) === 0

export interface DateRangePickerProps extends Omit<
  AriaDateRangePickerProps<DateValue>,
  | 'isDisabled'
  | 'isReadOnly'
  | 'isRequired'
  | 'isInvalid'
  | 'className'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'isOpen'
  | 'defaultOpen'
  | 'onOpenChange'
  | 'shouldCloseOnSelect'
> {
  value?: Range
  defaultValue?: Range
  onChange?: (value: Range) => void
  size?: FieldSize
  status?: FieldStatus
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  invalid?: boolean
  /** Month + year dropdowns in the calendar header. On by default; `false` for a plain label. */
  monthYearSelect?: boolean
  /** Quick-select column on the right. `true` uses the built-in set, or pass your own. */
  presets?: boolean | DateRangePreset[]
  /** Add an Apply / Cancel footer that gates the commit until Apply. */
  withApply?: boolean
  className?: string
}

export const DateRangePicker = React.forwardRef<
  HTMLDivElement,
  DateRangePickerProps
>(
  (
    {
      value,
      defaultValue,
      onChange,
      size = 'md',
      status,
      disabled,
      readOnly,
      required,
      invalid,
      monthYearSelect = true,
      presets,
      withApply = false,
      className,
      ...props
    },
    ref
  ) => {
    const resolved = invalid ? 'error' : (status ?? 'default')
    const iconSize =
      size === 'sm' ? 'size-6' : size === 'lg' ? 'size-8' : 'size-7'

    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState<Range>(defaultValue ?? null)
    const committed = isControlled ? value : internal
    const [open, setOpen] = React.useState(false)
    const [draft, setDraft] = React.useState<Range>(committed ?? null)
    // Relative default presets, seeded once and refreshed on open (not custom
    // arrays, which the consumer owns).
    const [autoPresets, setAutoPresets] = React.useState<DateRangePreset[]>(
      () => (presets === true ? buildDefaultPresets() : [])
    )

    // While closed, keep the draft in step with the committed value so an
    // external (controlled) change isn't masked by a stale draft.
    React.useEffect(() => {
      if (withApply && !open) setDraft(committed ?? null)
    }, [committed, open, withApply])

    // In Apply mode the calendar edits `draft`; otherwise it commits live.
    const activeValue = withApply ? draft : (committed ?? null)

    const commit = (next: Range) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    }

    const handleAriaChange = (next: Range) => {
      if (withApply) setDraft(next)
      else commit(next)
    }

    const handleOpenChange = (next: boolean) => {
      if (next) {
        if (withApply) setDraft(committed ?? null)
        // Recompute relative presets on open so "Today" et al. track the real
        // date even if the picker stays mounted across midnight.
        if (presets === true) setAutoPresets(buildDefaultPresets())
      }
      setOpen(next)
    }

    const pickPreset = (range: RangeValue<DateValue>) => {
      if (withApply) {
        setDraft(range)
      } else {
        commit(range)
        setOpen(false)
      }
    }

    const applyDraft = () => {
      commit(draft)
      setOpen(false)
    }
    const cancelDraft = () => {
      setDraft(committed ?? null)
      setOpen(false)
    }

    // Default (`presets === true`) presets are relative to "now", so they're
    // computed once at mount and refreshed on each open (see handleOpenChange)
    // — never rebuilt on every render, never stale across midnight. Custom
    // preset arrays are consumer-owned and pass straight through.
    const resolvedPresets: DateRangePreset[] | null =
      presets === true ? autoPresets : Array.isArray(presets) ? presets : null

    return (
      <AriaDateRangePicker
        ref={ref}
        value={activeValue}
        onChange={handleAriaChange}
        isOpen={open}
        onOpenChange={handleOpenChange}
        shouldCloseOnSelect={!withApply}
        isDisabled={disabled}
        isReadOnly={readOnly}
        isRequired={required}
        isInvalid={invalid}
        className={cn('flex flex-col gap-1.5', className)}
        {...props}
      >
        <Group
          className={cn(groupBase, groupSize[size], groupStatus[resolved])}
        >
          <div className="flex flex-1 items-center gap-1.5">
            <AriaDateInput slot="start" className="flex items-center">
              {(segment) => <DateSegment segment={segment} />}
            </AriaDateInput>
            <span aria-hidden="true" className="text-fbui-gray-400">
              –
            </span>
            <AriaDateInput slot="end" className="flex items-center">
              {(segment) => <DateSegment segment={segment} />}
            </AriaDateInput>
          </div>
          <AriaButton
            aria-label="Open calendar"
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-md text-fbui-gray-500 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[hovered]:text-fbui-gray-700 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand data-[disabled]:text-fbui-gray-300',
              iconSize
            )}
          >
            <CalendarBlank className="size-4" aria-hidden="true" />
          </AriaButton>
        </Group>
        <Popover
          placement="bottom start"
          offset={8}
          className="z-50 rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-3 shadow-fbui-lg outline-none"
        >
          <Dialog className="outline-none">
            <div className="flex gap-3">
              <div className="flex flex-col gap-3">
                <RangeCalendar monthYearSelect={monthYearSelect} />
                {withApply && (
                  <div className="flex justify-end gap-2 border-t border-fbui-gray-200 pt-3">
                    <Button
                      variant="primary"
                      appearance="outline"
                      size="sm"
                      onClick={cancelDraft}
                    >
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={applyDraft}>
                      Apply
                    </Button>
                  </div>
                )}
              </div>
              {resolvedPresets && (
                <div className="flex w-36 flex-col gap-0.5 border-l border-fbui-gray-200 pl-3">
                  {resolvedPresets.map((preset) => {
                    const active = sameRange(preset.value, activeValue)
                    return (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => pickPreset(preset.value)}
                        className={cn(
                          'w-full rounded-md px-3 py-1.5 text-left text-sm text-fbui-gray-700 outline-none transition-colors hover:bg-fbui-gray-100 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
                          active &&
                            'bg-fbui-basil-50 font-medium text-fbui-basil-700'
                        )}
                      >
                        {preset.label}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </Dialog>
        </Popover>
      </AriaDateRangePicker>
    )
  }
)
DateRangePicker.displayName = 'DateRangePicker'
